import {
  FC, useEffect, useRef, useCallback, useMemo,
} from "react";
import styled from "styled-components";
import { Spinner } from "@xfrontend/xui-react-plus";

const SpinnerWrapper = styled.div`
  display: flex;
  padding: 8px 0px;
  justify-content: center;
`;

const EndOfResultsText = styled.div`
  padding: 8px 0px;
  text-align: center;
  color: var(--x-purple-400);
`;

const LoadMoreText = styled.div`
  padding: 8px 0px;
  text-align: center;
  color: var(--x-purple-400);
`;

type InfiniteLoaderProps = {
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  observerRoot?: HTMLElement | null;
  endText?: string;
  threshold?: number;
  rootMargin?: string;
};

const InfiniteLoader: FC<InfiniteLoaderProps> = ({
  loading,
  hasMore,
  onLoadMore,
  observerRoot,
  endText,
  threshold = 0.8,
  rootMargin = "0px 0px 0px 0px",
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const actualRoot = useMemo<HTMLElement | null>(
    () => observerRoot ?? wrapperRef.current?.parentElement ?? null,
    [observerRoot],
  );

  const handleObserved = useCallback<IntersectionObserverCallback>(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        onLoadMore();
      }
    },
    [onLoadMore],
  );

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    observerRef.current = new IntersectionObserver(handleObserved, {
      root: actualRoot,
      rootMargin,
      threshold,
    });
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [actualRoot, threshold, rootMargin, handleObserved]);

  useEffect(() => {
    const target = loadMoreRef.current;
    const observer = observerRef.current;
    if (!observer || !target) return undefined;

    if (!loading && hasMore) {
      observer.observe(target);
    } else {
      observer.unobserve(target);
    }
    return () => {
      observer.unobserve(target);
    };
  }, [loading, hasMore]);

  return (
    <div ref={wrapperRef}>
      {loading ? (
        <SpinnerWrapper>
          <Spinner animation="border" variant="primary" />
        </SpinnerWrapper>
      ) : hasMore ? (
        <LoadMoreText ref={loadMoreRef}>Load More</LoadMoreText>
      ) : endText ? (
        <EndOfResultsText>{endText}</EndOfResultsText>
      ) : null}
    </div>
  );
};

export default InfiniteLoader;
